import * as React from 'react';
import type { BevelRenderSettings } from '../utils/getBevelDepth';

const ALPHA_TO_COLOR = '0 0 0 1 0  0 0 0 1 0  0 0 0 1 0  0 0 0 0 1';
const COLOR_TO_ALPHA = '0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  1 0 0 0 0';
const BOX = Array(9)
  .fill(1 / 9)
  .join(' ');
const EASE_OUT = Array.from(
  { length: 257 },
  (_, i) => 1 - (1 - i / 256) ** 2
).join(' ');

// This refinement covers the independently tested low-Range, Linear-gloss
// Dodge/Burn subset. Other contour/blend semantics retain their existing path.
export function usesNativeBevelSurface(depth: BevelRenderSettings) {
  return (
    depth.documentSize >= 1 &&
    depth.contourRange <= 25 &&
    depth.glossContour === 'linear' &&
    depth.highlightBlendMode === 'color-dodge' &&
    depth.shadowBlendMode === 'linear-burn'
  );
}

export function bevelSoftenSigma(depth: BevelRenderSettings) {
  if (!usesNativeBevelSurface(depth) || !depth.softness) return 0;
  const authored = depth.documentSoftness;
  // Empirical native mask footprint, including the existing antialias variance.
  // Native Soften 1 and 2 controls are identical. Keep this conversion inside
  // rendering; the authored Soften value is never rewritten.
  return (
    Math.sqrt(
      Math.max(0.66 ** 2, (authored <= 2 ? 0 : authored / 2) ** 2 - 0.75 ** 2)
    ) * depth.renderScale
  );
}

type Level = { radius: number; weight: number; rectangles: number[][] };
const footprints = new Map<string, Level[]>();
export function distanceBevelLevels(size: number, offset: number) {
  const key = `${size}:${offset}`;
  const cached = footprints.get(key);
  if (cached) return cached;
  const levels: Level[] = [];
  for (let radius = 0; radius <= Math.ceil(size + offset + 0.5); radius++) {
    const weight =
      Math.max(
        0,
        Math.min(radius + 0.5, size + offset) -
          Math.max(radius ? radius - 0.5 : 0, offset)
      ) / size;
    if (!weight) continue;
    const rectangles: number[][] = [];
    let previousY = -1;
    // A raster disk is the union of these maximal centred rectangles.
    // Erosion by their union is the minimum of their independent erosions.
    for (let x = radius; x >= 0; x--) {
      const y = Math.floor(Math.sqrt(radius * radius - x * x));
      if (y === previousY) continue;
      previousY = y;
      rectangles.push([x, y]);
    }
    levels.push({ radius, weight, rectangles });
  }
  if (footprints.size >= 64) {
    const oldest = footprints.keys().next().value;
    if (oldest !== undefined) footprints.delete(oldest);
  }
  footprints.set(key, levels);
  return levels;
}

export function getWideBevelSampling(
  depth: BevelRenderSettings,
  pixelRatio: number
) {
  const physicalSize = depth.size * pixelRatio;
  if (
    !usesNativeBevelSurface(depth) ||
    depth.documentSize <= 1 ||
    physicalSize < 1 ||
    physicalSize > 64
  )
    return null;
  const ring = depth.contour === 'ring';
  const smooth = depth.technique === 'smooth';
  return {
    levels: distanceBevelLevels(physicalSize, ring ? 0 : -0.5),
    pixelRatio,
    seedBlur: smooth ? 0.35 / pixelRatio : 0,
    surfaceBlur: smooth || ring ? 0.5 / pixelRatio : 0,
    smooth,
    surfaceScale:
      depth.surfaceScale *
      (ring ? 2 : 1.5) *
      ((ring ? 15 : 22) / depth.contourRange),
  };
}

type Sampling = NonNullable<ReturnType<typeof getWideBevelSampling>>;
// Integrate nested circular erosions into a continuous inward height ramp.
// Range controls relief strength here, rather than shrinking authored width.
// The glyph is never expanded, recoloured or embedded in the saved template.
export const WideBevelSurface = React.memo(
  ({ sampling }: { readonly sampling: Sampling }) => {
    let accumulator = 'wideDistanceZero';
    return (
      <>
        {sampling.seedBlur > 0 && (
          <feGaussianBlur
            in="SourceAlpha"
            stdDeviation={sampling.seedBlur}
            result="wideSeedBlur"
          />
        )}
        <feColorMatrix
          in={sampling.seedBlur ? 'wideSeedBlur' : 'SourceAlpha'}
          values={ALPHA_TO_COLOR}
          result="wideDistanceSeed"
        />
        <feColorMatrix
          in="wideDistanceSeed"
          values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0 1"
          result="wideDistanceZero"
        />
        {sampling.levels.map(({ radius, weight, rectangles }) => {
          let minimum = 'wideDistanceSeed';
          const nodes: React.ReactNode[] = [];
          if (radius)
            for (const [x, y] of rectangles) {
              const name = `wideDisk${radius}_${x}`;
              nodes.push(
                <feMorphology
                  key={name}
                  in="wideDistanceSeed"
                  operator="erode"
                  radius={`${x / sampling.pixelRatio} ${
                    y / sampling.pixelRatio
                  }`}
                  result={name}
                />
              );
              if (minimum !== 'wideDistanceSeed') {
                const mixed = `${name}Min`;
                nodes.push(
                  <feBlend
                    key={mixed}
                    in={minimum}
                    in2={name}
                    mode="darken"
                    result={mixed}
                  />
                );
                minimum = mixed;
              } else minimum = name;
            }
          const next = `wideDistanceSum${radius}`;
          nodes.push(
            <feComposite
              key={next}
              in={accumulator}
              in2={minimum}
              operator="arithmetic"
              k2={1}
              k3={weight}
              result={next}
            />
          );
          accumulator = next;
          return nodes;
        })}
        <feColorMatrix
          in={accumulator}
          values={COLOR_TO_ALPHA}
          result="wideDistanceAlpha"
        />
        <WideBevelSurfaceFinish sampling={sampling} input="wideDistanceAlpha" />
      </>
    );
  }
);

export function WideBevelSurfaceFinish({
  sampling,
  input,
}: {
  readonly sampling: Sampling;
  readonly input: string;
}) {
  return (
    <>
      {sampling.smooth && (
        <feComponentTransfer in={input} result="wideRoundedHeight">
          <feFuncA type="table" tableValues={EASE_OUT} />
        </feComponentTransfer>
      )}
      {sampling.surfaceBlur ? (
        <feGaussianBlur
          in={sampling.smooth ? 'wideRoundedHeight' : input}
          stdDeviation={sampling.surfaceBlur}
          result="surface"
        />
      ) : (
        <feConvolveMatrix
          in={input}
          order={3}
          kernelMatrix={BOX}
          divisor={1}
          edgeMode="none"
          result="surface"
        />
      )}
    </>
  );
}
